// Core modules

import { inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';

// External modules
import { catchError, delay, filter, fromEvent, lastValueFrom, map, merge, Observable, of, Subscription, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { ModalDismissReasons, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UserIdleConfig, UserIdleService } from 'angular-user-idle';

// Tokens
import { WINDOW } from '@core/token/tokens';

// Services
import { AccessControlService } from '@core/services/access-control/access-control.service';
import { AuthService } from '@core/services/auth/auth.service';

// Component
import { SessionExpiryNotificationComponent } from '@shared/components/session-expiry-notification/session-expiry-notification.component';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { UserProfileFieldsEnum } from '@shared/enum/user-profile-fields.enum';

/**
 * Session Manager Service
 */
@Injectable({
  providedIn: 'root',
})
export class SessionManagerService implements OnDestroy {
  // Injectors
  private readonly accessControlService = inject(AccessControlService);
  private readonly authService = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly ngbModal = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly userIdleService = inject(UserIdleService);
  private readonly window = inject(WINDOW);

  // Private Members
  private allowForceLogout = true;
  private logoutNotifierModalRef?: NgbModalRef;
  private modalOption: NgbModalOptions = {};
  private sessionExpiryNotifierModalIsOpen = false;
  private readonly subscription = new Subscription();

  /**
   * This method is helper method for ngBootstrap modal window to know the Dismiss Reason
   * Whether ESC Key is pressed or user clicked continue or logout button
   * @private
   * @param {*} reason
   * @return {*}  {string}
   * @memberof SessionManagerService
   */
  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'PRESS_ESC_BUTTON';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'BACKDROP_CLICK';
    } else {
      return `with: ${reason}`;
    }
  }

  private onVisibilityChange(): void {
    if (this.document.visibilityState === 'visible') {
      timer(100).subscribe(() => {
        localStorage.setItem('sessionActive', 'true');
      });
      this.subscription.add(
        this.authService.isSessionValid().subscribe((status) => {
          if (status) this.userIdleService.startWatching();
          else {
            this.stopWatching();
          }
        })
      );
    } else {
      localStorage.setItem('sessionActive', 'false');
      timer(500)
        .pipe(
          filter(() => localStorage.getItem('sessionActive') !== 'false'),
          tap(() => {
            this.userIdleService.stopTimer();
            this.userIdleService.stopWatching();
          })
        )
        .subscribe();
    }
  }

  /**
   * This method is used to indicate the user that currently in user idle state.
   *
   * @memberof SessionManagerService
   */
  private sessionExpiryNotifier(): void {
    this.allowForceLogout = true;
    this.modalOption = {
      backdrop: 'static',
      keyboard: false,
      backdropClass: 'modal-backdrop-opacity',
      windowClass: 'session-notifier-modal',
    };
    this.logoutNotifierModalRef = this.ngbModal.open(SessionExpiryNotificationComponent, this.modalOption);
    const userEmail = this.authService.getUserProfileBasedOnFields([UserProfileFieldsEnum.EMAIL]).email;
    this.logoutNotifierModalRef.componentInstance.userName = userEmail ? userEmail : '';
    this.logoutNotifierModalRef.result.then(
      (result) => {
        this.allowForceLogout = false;
        if (result === 'logout') {
          this.stopWatching();
          this.authService.logout();
        } else {
          this.userIdleService.stopTimer();
          this.subscription.add(
            this.authService.refreshSession().subscribe({
              next: () => {
                console.log('<Session-Manager> <sessionExpiryNotifier> :: Resetting UserIdle Timer :');
                this.userIdleService.resetTimer();
                this.sessionExpiryNotifierModalIsOpen = false;
              },
              error: (error) => {
                console.log('<Session-Manager> <sessionExpiryNotifier>: Failed while refreshing the session', error);
                this.stopWatching();
                this.subscription.unsubscribe();
                this.authService.logout();
              },
            })
          );
        }
      },
      (reason) => {
        console.log(`Dismissed ${this.getDismissReason(reason)}`);
        this.stopWatching();
        this.subscription.unsubscribe();
        this.authService.logout();
      }
    );
  }

  /**
   * Periodically Polling for Session is Alive
   * The poll interval between checks to checkSession() should be at least 10 minutes
   * between calls to avoid any issues in the future with rate limiting of this call
   * @memberof SessionManagerService
   */
  public setupSessionEvents(): void {
    const userIdleConfig: UserIdleConfig = this.userIdleService.getConfigValue();
    console.log(`<Session-Manager> <setupSessionEvents> :: Current User Idle Configuration is : ${JSON.stringify(userIdleConfig)}, at ${new Date()}`);

    // Set custom activity events
    const windowEvents = ['mousemove', 'mousedown', 'mouseup', 'resize', 'scroll', 'focus', 'wheel', 'hashchange', 'popstate'].map((event) => fromEvent(this.window, event));

    const documentEvents = [
      'keydown',
      'keyup',
      'keypress',
      'touchstart',
      'touchend',
      'touchmove',
      'click',
      'dblclick',
      'contextmenu',
      'pointerdown',
      'pointerup',
      'pointermove',
      'paste',
      'copy',
      'cut',
      'drag',
      'dragstart',
      'dragend',
      'drop',
      'input',
      'change',
    ].map((event) => fromEvent(this.document, event));

    const allEvents$ = merge(...windowEvents, ...documentEvents);
    this.userIdleService.setCustomActivityEvents(allEvents$);

    // Start watching for user inactivity in the active tab
    this.userIdleService.resetTimer();
    this.userIdleService.startWatching();
    if (this.document.visibilityState !== 'visible') {
      this.userIdleService.stopTimer();
      this.userIdleService.stopWatching();
    }

    // Bind to the visibilitychange event
    this.authService.visibilityListener = this.onVisibilityChange.bind(this);
    // Detect when this tab becomes active or inactive
    this.document.addEventListener('visibilitychange', this.authService.visibilityListener);
    // Start watching when user idle is starting
    this.subscription.add(
      this.userIdleService.onTimerStart().subscribe((count) => {
        console.log(`<Session-Manager> <setupSessionEvents> User Idle Timeout count :: ${new Date()}`, count);
        if (count && !this.sessionExpiryNotifierModalIsOpen) {
          console.log(`<Session-Manager> <setupSessionEvents> User Idle Timeout is Started !... ${new Date()}`);
          this.sessionExpiryNotifier();
          this.sessionExpiryNotifierModalIsOpen = true;
        }
      })
    );

    this.subscription.add(
      this.userIdleService
        .onTimeout()
        .pipe(delay(10 * 1000)) // 10 Seconds delay to show the modal before force logout so that user can see the counters in the modal
        .subscribe((isTimeout: boolean) => {
          console.log(`<Session-Manager> <setupSessionEvents> User Idle Timeout :: ${new Date()}`, isTimeout);
          if (isTimeout && this.allowForceLogout) {
            console.log(`<Session-Manager> <setupSessionEvents> Did not receive any input from user after Max User Idle Timeout. Force Logout on ${new Date()}`);
            this.sessionExpiryNotifierModalIsOpen = false;
            this.logoutNotifierModalRef?.close();
            this.stopWatching();
            console.log('<Session-Manager> <setupSessionEvents> service unsubscribed...');
            this.subscription.unsubscribe();
            this.authService.logout();
            console.log('<Session-Manager> <setupSessionEvents> service after logout...');
          }
        })
    );

    // Detect when user idle status is changed to show some logs, this is optional and is for local debugging purpose
    // this.subscription.add(
    //   this.userIdleService.onIdleStatusChanged().subscribe((isIdle) => {
    //     console.log(`<Session-Manager> <setupSessionEvents> User Idle Status :: ${new Date()}`, isIdle);
    //     if (isIdle) {
    //       console.log('<Session-Manager> <setupSessionEvents> User is INACTIVE now');
    //     } else {
    //       console.log('<Session-Manager> <setupSessionEvents> User is ACTIVE now');
    //     }
    //   })
    // );

    // Ping periodically for token refresh
    this.subscription.add(
      this.userIdleService.ping$.subscribe(async () => {
        console.log('PING', new Date());
        try {
          const tokenRotationRequired = this.document.visibilityState === 'visible';
          console.log('<Session-Manager> <setupSessionEvents> visibilityState of current document ....', this.document.visibilityState);
          await lastValueFrom(
            this.authService
              .refreshSession(tokenRotationRequired)
              .pipe(tap(() => console.log('<Session-Manager> <setupSessionEvents> After Token Refresh ....', new Date())))
              .pipe(
                takeUntil(
                  // stop polling on either logout
                  merge(this.authService.logoutSubject$.pipe(filter((status) => status)))
                )
              )
          );
          // Pick up scope changes an admin made mid-session. `refreshPermissions()` swallows its own
          // failures and emits false, so it can never reach the catch below and end the session there.
          // (`BACKGROUND_REFRESH` only opts the request out of the interceptor's `/auth/me` force-logout —
          // a 401 still runs the interceptor's re-authentication handler like any other request.)
          await lastValueFrom(this.accessControlService.refreshPermissions());
        } catch (error) {
          console.log('<Session-Manager> <setupSessionEvents> Failed while refreshing the session', error);
          this.stopWatching();
          this.authService.logout();
        }
      })
    );
  }

  /**
   * Stop the watching timer and unsubscribe the subscription
   */
  public stopWatching(): void {
    this.userIdleService.resetTimer();
    this.userIdleService.stopTimer();
    this.userIdleService.stopWatching();
    this.subscription.unsubscribe();
    this.allowForceLogout = true;
    console.log('<Session-Manager> <stopWatching>: Unsubscribed the ping and refresh, check session calls');
  }
  /**
   * This utility method is used to intercept all the route on hook of canActivateRoute
   *
   * @return {*}
   * @memberof SessionManagerService
   */
  public validateSessionBeforeCanActivateRoute(): Observable<boolean> {
    return this.authService.isSessionValid().pipe(
      take(1),
      switchMap((status) => {
        if (status) {
          console.log('<Session-Manager> <ValidateSessionBeforeCanActivateRoute>: current session status...', status);
          return of(true);
        } else {
          this.authService.authenticationInProgressSubject.next(true);
          this.authService.clientNavigationInProgressSubject.next(true);
          return lastValueFrom(
            this.authService.refreshSession().pipe(
              take(1),
              map(() => {
                this.authService.authenticationInProgressSubject.next(false);
                this.authService.clientNavigationInProgressSubject.next(false);
                console.log('<Session-Manager> <ValidateSessionBeforeCanActivateRoute>: After check session :', new Date());
                return true;
              })
            )
          );
        }
      }),
      catchError((error) => {
        this.authService.authenticationInProgressSubject.next(false);
        this.authService.clientNavigationInProgressSubject.next(false);
        console.log('<Session-Manager> <ValidateSessionBeforeCanActivateRoute>: Failed while refreshing the session', error);
        this.stopWatching();
        this.subscription.unsubscribe();
        this.authService.logout();
        return this.router.navigate([`/${MainNavigationEnum.LOGIN}`]);
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
